/**
 * OS-wide activity tracking for presence — the desktop half of auto-away.
 *
 * `activity.ts` watches DOM input, which by construction only covers our own
 * window: someone with the dashboard open beside their mail client went "away"
 * after five minutes of not touching *us*, even though they never left the
 * desk. This module closes that gap on the desktop shell by feeding the shell's
 * OS idle clock (`system_idle_seconds` / the `xop://system-idle` event, see
 * `src-tauri/src/idle.rs`) into {@link IdleWatcher.noteSystemIdle}, so moving
 * the mouse in ANY application keeps you showing active.
 *
 * A plain browser tab has no equivalent API, so this is a no-op there and DOM
 * input remains the only signal — an intentional, documented asymmetry (the
 * server's cross-device aggregation means a user who ALSO runs the desktop app
 * still shows active from it while their web tab idles).
 *
 * Two feeds, because each covers a gap the other leaves:
 *   - a **seed** read on mount, so a webview that just reconnected reflects the
 *     machine's real state immediately instead of waiting a poll interval;
 *   - a **push** subscription to the shell's Rust-side sampler, which keeps
 *     running at full rate while the app sits hidden in the tray (a JS timer
 *     would be throttled there — exactly when this matters most).
 */
import { getSystemIdleSeconds, onSystemIdle } from "./desktopBridge.js";

/** Injectable seams so the wiring is unit-testable without a Tauri shell. */
export interface SystemIdleTrackingOptions {
  /** One-shot read of the OS idle clock. Defaults to the desktop bridge. */
  read?: () => Promise<number | null>;
  /** Subscribe to pushed samples. Defaults to the desktop bridge. */
  subscribe?: (cb: (seconds: number) => void) => Promise<() => void>;
}

/**
 * Start feeding OS idle samples to `onSample`. Returns a stop function; it is
 * safe to call before the async wiring settles (a late subscription is torn
 * down immediately and no sample is delivered after stopping).
 */
export async function startSystemIdleTracking(
  onSample: (seconds: number) => void,
  opts: SystemIdleTrackingOptions = {},
): Promise<() => void> {
  const read = opts.read ?? getSystemIdleSeconds;
  const subscribe = opts.subscribe ?? onSystemIdle;

  let stopped = false;
  let unlisten: (() => void) | null = null;
  const deliver = (seconds: number): void => {
    if (!stopped) onSample(seconds);
  };

  const seeded = read()
    .then((secs) => {
      if (secs !== null) deliver(secs);
    })
    .catch(() => undefined);

  const listening = subscribe(deliver)
    .then((un) => {
      if (stopped) un();
      else unlisten = un;
    })
    .catch(() => undefined);

  await Promise.all([seeded, listening]);

  return () => {
    stopped = true;
    unlisten?.();
    unlisten = null;
  };
}
